import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import * as Plyr from 'plyr';
import {TtsService} from '../../../services/tts.service';
import {DatePipe} from '@angular/common';
import {FileSaverService} from 'ngx-filesaver';
import {TtsOutputFormatType} from '../../../model/tts.enum';
import {factory} from '../../../shared/app-logging';

const logger = factory.getLogger('ContentPlayerComponent');

export class ContentPlayerOptions {
  constructor(public filename?: string, public addSuffixDatetime?: boolean) {
  }
}

@Component({
  selector: 'app-content-player',
  templateUrl: './content-player.component.html',
  styleUrls: ['./content-player.component.css']
})
export class ContentPlayerComponent implements OnInit {

  // tslint:disable-next-line:no-input-rename
  @Input('for')
  elContentId: string;

  // tslint:disable-next-line:no-input-rename
  @Input('text')
  audioText: string;

  // tslint:disable-next-line:no-input-rename
  @Input('locale')
  audioLocale: string;

  // tslint:disable-next-line:no-input-rename
  @Input('voice')
  audioVoice: string;

  // tslint:disable-next-line:no-input-rename
  @Input('controls')
  controlsType: string;

  // tslint:disable-next-line:no-input-rename
  @Input('download')
  canDownload?: boolean;

  // tslint:disable-next-line:no-output-rename
  @Output('load')
  private loadEmitter: EventEmitter<CustomEvent> = new EventEmitter<CustomEvent>();

  public contentPlyr: Plyr;

  audioLoaded: boolean;
  audioLoading: boolean;
  audioDownloading: boolean;

  private audioBlob: Blob;

  public outputFormatTypeSel: TtsOutputFormatType = TtsOutputFormatType.MP3;

  private plyrControlsPlPgMt = `
  <div class="plyr__controls" style="line-height: 1em; margin-top: -5px">
    <button type="button" class="plyr__control" aria-label="Play, {title}" data-plyr="play">
        <a class="icon--pressed fa fa-pause" role="presentation"><use xlink:href="#plyr-pause"></use></a>
        <a class="icon--not-pressed fa fa-play" role="presentation"><use xlink:href="#plyr-play"></use></a>
        <!--<span class="label&#45;&#45;pressed plyr__tooltip" role="tooltip">Pause</span>-->
        <!--<span class="label&#45;&#45;not-pressed plyr__tooltip" role="tooltip">Play</span>-->
    </button>
    <div class="plyr__progress">
        <input data-plyr="seek" type="range" min="0" max="100" step="0.01" value="0" aria-label="Seek">
        <progress class="plyr__progress__buffer" min="0" max="100" value="0">% buffered</progress>
        <span role="tooltip" class="plyr__tooltip">00:00</span>
    </div>
    <div class="plyr__time plyr__time--current" aria-label="Current time">00:00</div>
    <div class="plyr__time plyr__time--duration" aria-label="Duration">00:00</div>
    <button type="button" class="plyr__control" aria-label="Mute" data-plyr="mute">
        <a class="icon--pressed fa fa-volume-mute fa-lg" role="presentation"><use xlink:href="#plyr-muted"></use></a>
        <a class="icon--not-pressed fa fa-volume-up fa-lg" role="presentation"><use xlink:href="#plyr-volume"></use></a>
        <!--<span class="label&#45;&#45;pressed plyr__tooltip" role="tooltip">Unmute</span>-->
        <!--<span class="label&#45;&#45;not-pressed plyr__tooltip" role="tooltip">Mute</span>-->
    </button>
  </div>
  `;

  private plyrControlsPl = `
  <div class="plyr__controls" style="line-height: 1em;">
    <button type="button" class="plyr__control" aria-label="Play, {title}" data-plyr="play">
        <a class="icon--pressed fa fa-pause" role="presentation"><use xlink:href="#plyr-pause"></use></a>
        <a class="icon--not-pressed fa fa-play" role="presentation"><use xlink:href="#plyr-play"></use></a>
        <!--<span class="label--pressed plyr__tooltip" role="tooltip">Pause</span>-->
        <!--<span class="label--not-pressed plyr__tooltip" role="tooltip">Play</span>-->
    </button>
  </div>
  `;

  public plyrSources: Plyr.Source[];

  private audioOptions: ContentPlayerOptions;

  constructor(private ttsService: TtsService,
              private fileSaverService: FileSaverService,
              private datePipe: DatePipe) {
  }

  ngOnInit() {
    this.reset();
  }

  reset() {
    logger.debug('reset');
    this.contentPlyr = null;
    this.plyrSources = [];

    this.audioLoaded = false;
    this.audioLoading = false;
    this.audioDownloading = false;
  }

  public getControlsType() {
    switch (this.controlsType) {
      case 'Pl':
        return this.plyrControlsPl;
      case 'PlPgMt':
      default:
        return this.plyrControlsPlPgMt;
    }
  }

  public load(e: Event) {
    logger.debug(`Load event ${e}`);

    if (this.audioText && this.audioLocale && this.audioText.length > 0 && this.audioLocale.length > 0) {
      logger.debug(`Load event calling directly to TTS Service`);
      this.loadTts(this.audioText, this.audioLocale, this.audioVoice);
    } else {
      logger.debug(`Load event emitting custom event to be captured to call TTS Service`);
      this.loadEmitter.emit(new CustomEvent('load', {detail: {contentId: this.elContentId}}));
    }
  }

  loadTts(text: string, locale: string, voice?: string, options?: ContentPlayerOptions) {
    this.audioLoading = true;
    this.audioOptions = options;

    this.ttsService.getTts(text, TtsOutputFormatType.MP3, locale, voice).subscribe((data: Blob) => {
      logger.debug(`Audio binary content received`);
      this.audioLoading = false;
      this.plyrSources = [
        {
          src: URL.createObjectURL(data),
          type: 'audio/mp3'
        }
      ];

      this.audioLoaded = true;
      this.audioBlob = data;
    }, error => {
      logger.debug(`Audio load error`);
      this.audioLoaded = false;
      this.audioLoading = false;
      error.message = `While performing Text To Speech conversion. ${error.message}`;
      throw error;
    });
  }

  public downloadAudioFile(outputFormatType = TtsOutputFormatType.MP3) {
    const filename = this.formatAudioFilename(outputFormatType);

    if (outputFormatType !== TtsOutputFormatType.MP3) {
      logger.debug(`Download audio file (${filename}, request different format: ${outputFormatType}`);
      this.audioDownloading = true;

      this.ttsService.getTts(this.audioText, TtsOutputFormatType.MP3, this.audioLocale, this.audioVoice)
        .subscribe((data: Blob) => {
          logger.debug(`Audio download request successfully performed... saving file.`);
          this.audioDownloading = false;

          this.fileSaverService.save(data, filename);
        }, error => {
          logger.debug(`Audio download error`);
          this.audioDownloading = false;
          error.message = `While performing Text To Speech conversion. ${error.message}`;
          throw error;
        });
    } else {
      logger.debug(`Download MP3 audio file (${filename}`);
      this.fileSaverService.save(this.audioBlob, filename);
    }
  }

  private formatAudioFilename(outputFormatType = TtsOutputFormatType.MP3): string {
    let filename = 'AudioFile';

    if (this.audioOptions) {
      if (this.audioOptions.filename) {
        filename += this.audioOptions.filename;
      }

      if (this.audioOptions.addSuffixDatetime) {
        filename += '[' + this.datePipe.transform(Date.now(), 'yyyyMMddHHmm') + ']';
      }
    }

    filename += '.' + outputFormatType;

    return filename;
  }

}
